import React, { useState, useRef, useEffect } from 'react';
import Icon from './Icon';
import { Copy, Check } from 'lucide-react';

export default function MailWithPip({ name, href, size, style }) {
	// detectar táctil localmente
	const [isTouchDevice, setIsTouchDevice] = useState(false);
	useEffect(() => {
		setIsTouchDevice("maxTouchPoints" in navigator && navigator.maxTouchPoints > 0);
	}, []);

	const tooltipContent = typeof href === 'string' && href.toLowerCase().startsWith('mailto:')
		? href.replace(/^mailto:/i, '')
		: href;

	const mailHref = (typeof href === 'string' && href.toLowerCase().startsWith('mailto:')) ? href : `mailto:${tooltipContent}`;

	const [isPressed, setIsPressed] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [copied, setCopied] = useState(false);
	const copyTimeoutRef = useRef(null);

	useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
		};
	}, []);

	const handleCopyMail = async (e) => {
		e?.stopPropagation();
		try {
			if (typeof navigator !== 'undefined' && navigator.clipboard && tooltipContent) {
				await navigator.clipboard.writeText(String(tooltipContent));
				setCopied(true);
				if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
				copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
			}
		} catch (err) {
			// silenciar error en contextos no seguros
		}
	};

	const linkStyle = {
		...style,
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: isPressed ? "#e2e2e2ff" : (isHovered ? "rgba(0,0,0,0.5)" : "black"),
		filter: isPressed ? "grayscale(100%) brightness(85%)" : undefined,
		transition: "filter 120ms ease, color 120ms ease",
		outline: "none",
		WebkitTapHighlightColor: "transparent"
	};

	// cambios: gaps iguales (6), letra más pequeña, menos padding vertical
	return (
		<div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", marginTop: -4, lineHeight: 0 }}>
			<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
				{/* Icono */}
				<div style={{ background: "transparent", padding: 0, margin: 0 }}>
					<a
						href={mailHref}
						onClick={() => {}}
						onMouseDown={() => setIsPressed(true)}
						onMouseUp={() => setIsPressed(false)}
						onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
						onMouseLeave={() => { if (!isTouchDevice) { setIsHovered(false); setIsPressed(false); } }}
						onTouchStart={() => { setIsPressed(true); setIsHovered(false); }}
						onTouchEnd={() => { setIsPressed(false); setIsHovered(false); }}
						onTouchCancel={() => { setIsPressed(false); setIsHovered(false); }}
						onFocus={() => !isTouchDevice && setIsHovered(true)}
						onBlur={() => { setIsHovered(false); setIsPressed(false); }}
						onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') setIsPressed(true); }}
						onKeyUp={(e) => { if (e.key === ' ' || e.key === 'Enter') setIsPressed(false); }}
						aria-label={name}
						style={{
							...linkStyle,
							background: "transparent",
							border: "none",
							padding: 4, // menos padding
							margin: 0,
							textDecoration: "none",
							width: "auto",
							height: "auto"
						}}
					>
						<Icon name={name} size={size} />
					</a>
				</div>

				{/* Tooltip siempre visible a la derecha con piquito apuntando a la izquierda */}
				<div
					// prevenir selección manual: estilos + handlers para mouse/touch y onCopy
					style={{
						position: "relative",
						background: "#befd52bb",
						color: "black",
						padding: "3px 8px",         // menos padding vertical, horizontal reducido
						borderRadius: 6,
						whiteSpace: "nowrap",
						display: "inline-flex",
						alignItems: "center",
						gap: 6,                    // gaps igualitarios
						fontSize: 7,              // letra ligeramente más pequeña
						zIndex: 2,
						maxWidth: "none",
						userSelect: "none",
						WebkitUserSelect: "none",
						MozUserSelect: "none"
					}}
					role="group"
					aria-label="Correo"
				>
					<span style={{ display: "inline-block", userSelect: "none", whiteSpace: "nowrap", fontSize: 13 }}>
						{tooltipContent}
					</span>

					<button
						onClick={handleCopyMail}
						onMouseDown={(e) => e.preventDefault()}
						aria-label={copied ? "Copiado" : "Copiar"}
						title={copied ? "Copiado" : "Copiar"}
						style={{
							background: "transparent",
							border: "none",
							padding: 4,               // menos padding
							margin: 0,
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
							color: "black"
						}}
					>
						{copied ? <Check size={14} /> : <Copy size={14} />}
					</button>

					{/* Piquito apuntando a la izquierda */}
					<div
						aria-hidden="true"
						style={{
							position: "absolute",
							left: -10,                        // ajustar desplazamiento
							top: "50%",
							transform: "translateY(-50%)",
							width: 0,
							height: 0,
							borderTop: "6px solid transparent",    // menos altura => más puntiagudo
							borderBottom: "6px solid transparent",
							borderRight: `10px solid #befd52bb`,   // mayor anchura del triángulo
							pointerEvents: "none"
						}}
					/>
				</div>
			</div>
		</div>
	);
}
